import React, { useEffect, useMemo, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import viewStyles from '../../assets/styles/LayoutSharedView.module.css';
import styles from './ChatSyncorpPage.module.css';
import api from '../../services/api';
import PlayerAudio, { pararAudioAtivo } from './PlayerAudio';

/**
 * Laboratório de Interação — tela própria de mensageiro para falar direto
 * com o motor Syncorp pelo navegador (história "escolher projeto,
 * participante e modo de uso antes de iniciar uma conversa Web", Task
 * 1/N — evolução da tela "Chat Syncorp" da história anterior). Fala pelo
 * MESMO caminho de qualquer canal Web (eventos "interacao"/"conversa:*" do
 * Socket.IO, ver src/messaging/adapters/webSocketHandler.ts e
 * webConversaLifecycle.ts no inteagro-node) — nenhuma infraestrutura de
 * WhatsApp entra aqui.
 *
 * Não é a mesma tela que "Mensagens" (src/pages/Mensagens): aquela é a
 * caixa de atendimento humano das conversas de WhatsApp; esta é uma
 * conversa direta do usuário do painel com o motor (Pesca/LIDA), sem
 * pescador do outro lado — por isso o visual (paleta, rótulos "Você" /
 * "Syncorp") é deliberadamente diferente, pra nunca serem confundidas.
 *
 * "Conversa só inicia com contexto válido" (critério de aceite): domínio,
 * projeto e participante ficam num painel de contexto SEMPRE visível (não
 * mais escondido atrás de um botão "+ Nova conversa") — escolher um
 * participante só o SELECIONA; só o clique em "Iniciar conversa" dispara
 * conversa:iniciar, e o backend valida projeto/participante de novo antes
 * de aceitar (webConversaLifecycle.ts nunca confia só no que o painel já
 * filtrou).
 *
 * "modo de uso" / dataScope (história "...e modo de uso antes de iniciar
 * uma conversa Web", Task 2/N): Produção, Demonstração ou Teste — default
 * "Teste" de propósito (só admin pode operar em Produção pelo Laboratório;
 * ver autorizarDataScope no backend). Fixado na CRIAÇÃO da conversa e
 * nunca muda depois — retomar uma conversa já existente sempre mostra o
 * modo VERDADEIRO dela (devolvido pelo backend), nunca o que estava
 * selecionado no painel antes de clicar.
 *
 * Task 4/N ("...auditoria completa, sessões isoladas e modo não-produção
 * inequívoco"):
 *   - "Nova demonstração" (só fora de Produção) inicia uma sessão ISOLADA
 *     pro mesmo participante — nunca reaproveita histórico/estado de uma
 *     demonstração anterior. Cada conversa aberta guarda seu próprio
 *     `conversationId`, ecoado de volta em toda ação seguinte (mensagem,
 *     encerrar, reiniciar) pra nunca ambiguar com a conversa "padrão".
 *   - "Iniciada por" mostra quem abriu cada conversa (auditoria) — vem do
 *     backend (tbl_conversa.uid_operador), nunca inventado aqui.
 *   - Uma faixa CHEIA (não só um selo) cobre o topo do painel de chat
 *     inteiro sempre que o modo não é Produção — inequívoco, impossível
 *     de confundir com uma conversa real rolando por cima dela.
 */

type StatusConversa = 'ativa' | 'em_atendimento_humano' | 'encerrada';
type Dominio = 'pesca' | 'agro';
type DataScope = 'producao' | 'demonstracao' | 'teste';

const ROTULO_DATA_SCOPE: Record<DataScope, string> = {
  producao: 'Produção',
  demonstracao: 'Demonstração',
  teste: 'Teste',
};

type ConversaResumo = {
  conversationId: string;
  actorId: string | null;
  nomeParticipante: string | null;
  projectId: string | null;
  nomeProjeto: string | null;
  status: StatusConversa;
  dataScope: DataScope;
  nomeOperador: string | null;
  ultimaInteracao: string | null;
};

type Pescador = { uid_pesca_pescador: string; nome: string; telefone?: string | null };
type Projeto = { uid_pesca_projeto: string; nome: string };

type EstadoMensagem = 'enviando' | 'enviada' | 'recebida' | 'erro';
type Mensagem = {
  id: string;
  autor: 'voce' | 'syncorp' | 'sistema';
  texto: string;
  timestamp: string;
  estado?: EstadoMensagem;
  opcoes?: string[];
  /** Mensagem NOSSA que foi um áudio gravado. */
  ehAudioEnviado?: boolean;
  /** Áudio guardado no servidor (histórico): buscado em /web/audio/:uidMensagem. */
  temAudioServidor?: boolean;
  /** Arquivo anexo (ex.: relatório PDF): buscado em /web/arquivo/:uidMensagem. */
  arquivo?: { uidMensagem: string; filename: string; mimeType: string };
  uidMensagem?: string;
  /** URL local (gravação própria) ou data URL (resposta falada) do áudio. */
  audioSrc?: string;
  /** Resposta falada recém-chegada: o PlayerAudio do balão inicia sozinho. */
  autoplay?: boolean;
  /** Transcrição usada pelo motor (entrada) ou texto falado (resposta). */
  transcricao?: string;
  falhaTranscricao?: boolean;
  /** Preview local (data URL) de uma imagem que NÓS enviamos — nunca da resposta do motor, que não devolve imagem. */
  imagemPreviewUrl?: string;
  /** Localização que NÓS enviamos. */
  localizacaoEnviada?: { latitude: number; longitude: number };
};

type RespostaAck<T> = { ok: true } & T | { ok: false; error: string };

/** Payload do evento Socket.IO "response" — objeto estruturado (ver helpers.ts:RespostaChatWeb no backend), nunca mais uma string solta com "Opções: a | b" pra decodificar via regex. */
type RespostaChatWeb = {
  text: string;
  options?: string[];
  audio?: { base64: string; mimeType: string };
  arquivo?: { uid_mensagem: string; filename: string; mimeType: string };
};

const AVISO_SEM_RESPOSTA_MS = 20000;
// Áudio e foto demoram mais (transcrição de áudio longo, upload): a espera
// é maior e só vira "Sem resposta" depois disso (antes, 20 s cortava áudio
// longo que ainda estava sendo processado).
const AVISO_AUDIO_MS = 330000;
const AVISO_IMAGEM_MS = 120000;
const DURACAO_MAX_AUDIO_MS = 120000;

let contadorLocal = 0;
function idLocal(): string {
  contadorLocal += 1;
  return `local-${Date.now()}-${contadorLocal}`;
}

function formatarHora(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

function formatarDataSeparador(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatarDataHora(iso?: string | null): string {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function chaveDia(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const ChatSyncorpPage = () => {
  const [conectado, setConectado] = useState(false);
  const [conversas, setConversas] = useState<ConversaResumo[]>([]);
  const [carregandoConversas, setCarregandoConversas] = useState(true);
  const [conversaAtiva, setConversaAtiva] = useState<ConversaResumo | null>(null);
  const [mensagens, setMensagens] = useState<Mensagem[]>([]);
  const [carregandoConversa, setCarregandoConversa] = useState(false);
  const [texto, setTexto] = useState('');
  const [aguardando, setAguardando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [mostrarConversaMobile, setMostrarConversaMobile] = useState(false);

  const [dominio] = useState<Dominio>('pesca');
  const [projetos, setProjetos] = useState<Projeto[]>([]);
  const [projetoSelecionado, setProjetoSelecionado] = useState('');
  const [buscaPescador, setBuscaPescador] = useState('');
  const [pescadores, setPescadores] = useState<Pescador[]>([]);
  const [pescadorSelecionado, setPescadorSelecionado] = useState<Pescador | null>(null);
  // Default "teste" de propósito — "producao" só é liberado pro admin (ver
  // autorizarDataScope no backend); começar seguro evita que o usuário
  // trombe com a recusa sem entender por quê.
  const [dataScopeSelecionado, setDataScopeSelecionado] = useState<DataScope>('teste');
  const ehAdmin = localStorage.getItem('modulo') === 'admin';

  const [gravando, setGravando] = useState(false);
  const [erroGravacao, setErroGravacao] = useState<string | null>(null);

  const socketRef = useRef<Socket | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const chatRef = useRef<HTMLDivElement | null>(null);
  const pertoDoFinalRef = useRef(true);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksAudioRef = useRef<Blob[]>([]);
  const timeoutGravacaoRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputImagemRef = useRef<HTMLInputElement | null>(null);
  const [enviandoLocalizacao, setEnviandoLocalizacao] = useState(false);

  // "Modo Apresentação": só visual (esconde o painel de contexto e mostra a
  // conversa numa moldura de celular). Não toca em socket nem em regra de
  // negócio. Só vale com conversa aberta.
  const [modoApresentacao, setModoApresentacao] = useState(false);
  // true quando o usuário saiu da tela cheia pelo botão "Tela cheia" — nesse
  // caso a apresentação continua (só o Esc/fullscreenchange externo encerra).
  const saidaTelaCheiaManualRef = useRef(false);
  const apresentando = modoApresentacao && !!conversaAtiva;

  const alternarTelaCheia = () => {
    if (document.fullscreenElement) {
      saidaTelaCheiaManualRef.current = true;
      document.exitFullscreen?.().catch(() => { saidaTelaCheiaManualRef.current = false; });
    } else {
      document.documentElement.requestFullscreen?.().catch(() => {});
    }
  };

  const iniciarApresentacao = () => {
    setModoApresentacao(true);
    document.documentElement.requestFullscreen?.().catch(() => {});
  };

  const sairDaApresentacao = () => setModoApresentacao(false);

  useEffect(() => {
    if (!apresentando) return undefined;
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setModoApresentacao(false);
      } else if (e.key === 'F11') {
        // Melhor esforço: alguns navegadores não deixam interceptar o F11
        // (nesse caso o F11 nativo também funciona, o layout só usa o viewport).
        e.preventDefault();
        alternarTelaCheia();
      }
    };
    const aoMudarTelaCheia = () => {
      if (document.fullscreenElement) return;
      if (saidaTelaCheiaManualRef.current) { saidaTelaCheiaManualRef.current = false; return; }
      // Esc (ou qualquer saída externa) da tela cheia encerra a apresentação.
      setModoApresentacao(false);
    };
    document.addEventListener('keydown', aoTeclar);
    document.addEventListener('fullscreenchange', aoMudarTelaCheia);
    return () => {
      document.removeEventListener('keydown', aoTeclar);
      document.removeEventListener('fullscreenchange', aoMudarTelaCheia);
      // Saiu da apresentação (ou da página) com a tela cheia ainda ativa: libera.
      if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apresentando]);

  const carregarConversas = async (socket: Socket) => {
    setCarregandoConversas(true);
    try {
      const resp = await socket.emitWithAck('conversa:listar', {});
      if (resp?.ok) setConversas(resp.conversas || []);
    } finally {
      setCarregandoConversas(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('inteagro_token');
    const socket = io(process.env.REACT_APP_API_URL as string, { auth: { token }, transports: ['websocket'] });
    socketRef.current = socket;

    socket.on('connect', () => {
      setConectado(true);
      carregarConversas(socket);
    });
    socket.on('disconnect', () => setConectado(false));
    socket.on('connect_error', (err) => {
      setConectado(false);
      setErro(`Não foi possível conectar: ${err.message}`);
    });
    socket.on('response', (resposta: RespostaChatWeb) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      setAguardando(false);
      setMensagens(prev => [
        ...prev,
        {
          id: idLocal(),
          autor: 'syncorp',
          texto: resposta?.text || '',
          timestamp: new Date().toISOString(),
          estado: 'recebida',
          opcoes: resposta?.options,
          audioSrc: resposta?.audio ? `data:${resposta.audio.mimeType};base64,${resposta.audio.base64}` : undefined,
          autoplay: resposta?.audio ? true : undefined,
          transcricao: resposta?.audio ? resposta?.text || '' : undefined,
          arquivo: resposta?.arquivo
            ? { uidMensagem: resposta.arquivo.uid_mensagem, filename: resposta.arquivo.filename, mimeType: resposta.arquivo.mimeType }
            : undefined,
        },
      ]);

      if (resposta?.transcricaoEntrada) {
        const transcricao = resposta.transcricaoEntrada;
        setMensagens(prev => {
          const copia = [...prev];
          for (let i = copia.length - 1; i >= 0; i--) {
            if (copia[i].ehAudioEnviado && !copia[i].transcricao) {
              copia[i] = { ...copia[i], transcricao };
              break;
            }
          }
          return copia;
        });
      }

      // "Entrada foi áudio -> resposta TEM que ser falada" (Task 1/N): o
      // balão leva `autoplay` e o PRÓPRIO PlayerAudio dá o play no seu
      // <audio> (instância única — nada de `new Audio()` solto, que ficava
      // fora do controle do player). Se o browser bloquear o autoplay, o
      // botão ▶ do balão toca manualmente.
    });

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (timeoutGravacaoRef.current) clearTimeout(timeoutGravacaoRef.current);
      socket.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    api.get('/pesca/projetos').then(res => setProjetos(res.data?.data || [])).catch(() => setProjetos([]));
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      const params = new URLSearchParams({ page: '1', search: buscaPescador, limit: '15' });
      // Projeto explícito filtra os participantes vinculados a ele (Laboratório, item 1).
      if (projetoSelecionado) params.set('uid_pesca_projeto', projetoSelecionado);
      api.get(`/pesca/pescadores?${params.toString()}`)
        .then(res => setPescadores(res.data?.data || []))
        .catch(() => setPescadores([]));
    }, 300);
    return () => clearTimeout(t);
  }, [buscaPescador, projetoSelecionado]);

  useEffect(() => {
    if (chatRef.current && pertoDoFinalRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [mensagens, aguardando]);

  const abrirConversa = async (
    actorId: string,
    projectId?: string,
    dataScope?: DataScope,
    opcoes: { conversationId?: string; novaSessao?: boolean } = {}
  ) => {
    const socket = socketRef.current;
    if (!socket || !conectado) return;

    pararAudioAtivo();
    setErro(null);
    setCarregandoConversa(true);
    setMostrarConversaMobile(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    setAguardando(false);

    try {
      const resp: RespostaAck<{
        conversationId: string;
        status: StatusConversa;
        dataScope: DataScope;
        projectId: string | null;
        nomeProjeto: string | null;
        historico: {
          uid_mensagem?: string;
          direcao: 'in' | 'out';
          tipo?: string;
          conteudo: string;
          timestamp: string;
          temAudio?: boolean;
          falhaTranscricao?: boolean;
          opcoes?: string[];
          arquivo?: { filename: string; mimeType: string };
        }[];
      }> =
        await socket.emitWithAck('conversa:iniciar', {
          domain: dominio,
          actorId,
          projectId,
          dataScope,
          conversationId: opcoes.conversationId,
          novaSessao: opcoes.novaSessao,
        });

      if (!resp.ok) {
        setErro(resp.error);
        return;
      }

      pertoDoFinalRef.current = true;
      setMensagens(
        resp.historico.map((m) => {
          const ehAudio = m.tipo === 'audio' && m.direcao === 'in';
          return {
            id: idLocal(),
            uidMensagem: m.uid_mensagem,
            autor: m.direcao === 'in' ? 'voce' : 'syncorp',
            texto: ehAudio ? '' : m.conteudo,
            timestamp: m.timestamp,
            ehAudioEnviado: ehAudio,
            temAudioServidor: m.temAudio,
            transcricao: ehAudio ? m.conteudo : undefined,
            falhaTranscricao: m.falhaTranscricao,
            opcoes: m.opcoes,
            arquivo: m.arquivo && m.uid_mensagem ? { uidMensagem: m.uid_mensagem, ...m.arquivo } : undefined,
          };
        })
      );

      // O modo de uso mostrado passa a ser o VERDADEIRO (devolvido pelo
      // backend) — pode diferir do que foi pedido se a conversa já
      // existia com outro dataScope (nunca sobrescrito, ver
      // conversaRepository.ts).
      setDataScopeSelecionado(resp.dataScope);

      const existente = conversas.find((c) => c.conversationId === resp.conversationId);
      setConversaAtiva(
        existente
          ? { ...existente, status: resp.status, dataScope: resp.dataScope, projectId: resp.projectId, nomeProjeto: resp.nomeProjeto }
          : {
              conversationId: resp.conversationId,
              actorId,
              nomeParticipante: null,
              projectId: resp.projectId,
              nomeProjeto: resp.nomeProjeto,
              status: resp.status,
              dataScope: resp.dataScope,
              nomeOperador: null,
              ultimaInteracao: new Date().toISOString(),
            }
      );

      // Conversa nova (não estava na lista ainda, ou é uma sessão nova
      // isolada) — recarrega pra ela aparecer com participante/projeto/
      // quem iniciou já resolvidos pelo backend.
      if (!existente) await carregarConversas(socket);
    } catch (e: any) {
      setErro(e?.message || 'Erro ao abrir a conversa.');
    } finally {
      setCarregandoConversa(false);
    }
  };

  const enviarMensagem = (mensagemTexto: string) => {
    const limpo = mensagemTexto.trim();
    const socket = socketRef.current;
    if (!limpo || !socket || !conectado || !conversaAtiva || aguardando) return;

    setMensagens(prev => [...prev, { id: idLocal(), autor: 'voce', texto: limpo, timestamp: new Date().toISOString(), estado: 'enviada' }]);
    setTexto('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
    pertoDoFinalRef.current = true;
    setAguardando(true);
    setErro(null);

    socket.emit('interacao', {
      domain: dominio,
      actorId: conversaAtiva.actorId,
      projectId: conversaAtiva.projectId || undefined,
      dataScope: conversaAtiva.dataScope,
      // Ecoa a conversa EXATA que está aberta (pode ser uma sessão nova/
      // específica, não a "padrão") — ver conversationIdPertenceAoAtor no
      // backend, que confere isso antes de aceitar.
      conversationId: conversaAtiva.conversationId,
      text: limpo,
    });

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setAguardando(false);
      setMensagens(prev => [...prev, { id: idLocal(), autor: 'sistema', texto: 'Sem resposta do motor Syncorp. Tente novamente em alguns instantes.', timestamp: new Date().toISOString() }]);
    }, AVISO_SEM_RESPOSTA_MS);
  };

  // Converte o Blob gravado pra base64 (sem o prefixo "data:...;base64,")
  // — é o formato que o backend espera (ver AudioWebInput em
  // src/messaging/adapters/web.ts do inteagro-node).
  // O arquivo exige o token (um <a href> não o envia): busca como blob e abre/baixa localmente.
  async function abrirArquivo(arq: { uidMensagem: string; filename: string; mimeType: string }, baixar: boolean) {
    // Abre a aba ANTES do await: depois dele o navegador bloqueia o pop-up.
    const aba = baixar ? null : window.open('', '_blank');
    try {
      const res = await api.get(`/web/arquivo/${arq.uidMensagem}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: arq.mimeType }));
      if (baixar) {
        const a = document.createElement('a');
        a.href = url;
        a.download = arq.filename;
        a.click();
      } else if (aba) {
        aba.location.href = url;
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      aba?.close();
      setErro('Não foi possível carregar o arquivo.');
    }
  }

  function blobParaBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const resultado = String(reader.result || '');
        resolve(resultado.split(',')[1] || '');
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  const enviarAudio = async (base64: string, mimeType: string, audioSrc: string) => {
    const socket = socketRef.current;
    if (!socket || !conectado || !conversaAtiva || aguardando) return;

    setMensagens(prev => [...prev, { id: idLocal(), autor: 'voce', texto: '', ehAudioEnviado: true, audioSrc, timestamp: new Date().toISOString(), estado: 'enviada' }]);
    pertoDoFinalRef.current = true;
    setAguardando(true);
    setErro(null);

    socket.emit('interacao', {
      domain: dominio,
      actorId: conversaAtiva.actorId,
      projectId: conversaAtiva.projectId || undefined,
      dataScope: conversaAtiva.dataScope,
      conversationId: conversaAtiva.conversationId,
      audio: { base64, mimeType },
    });

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setAguardando(false);
      setMensagens(prev => [...prev, { id: idLocal(), autor: 'sistema', texto: 'Sem resposta do motor Syncorp. Tente novamente em alguns instantes.', timestamp: new Date().toISOString() }]);
    }, AVISO_AUDIO_MS);
  };

  const iniciarGravacao = async () => {
    setErroGravacao(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // audio/webm;opus é o formato nativo do Chrome/Edge/Firefox; Safari
      // cai no default do MediaRecorder (geralmente mp4/aac) — o backend
      // aceita os dois (ver extensaoPorMimeType em webAudioFlow.ts, que só
      // repassa pro ffmpeg do lado Python).
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : '';
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      chunksAudioRef.current = [];

      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksAudioRef.current.push(e.data); };
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        if (timeoutGravacaoRef.current) { clearTimeout(timeoutGravacaoRef.current); timeoutGravacaoRef.current = null; }
        const blob = new Blob(chunksAudioRef.current, { type: recorder.mimeType || mimeType || 'audio/webm' });
        if (blob.size === 0) return;
        const base64 = await blobParaBase64(blob);
        enviarAudio(base64, blob.type, URL.createObjectURL(blob));
      };

      mediaRecorderRef.current = recorder;
      recorder.start();
      setGravando(true);

      // Trava de segurança — evita gravação infinita se o usuário esquecer
      // de parar (o pescador real também não manda áudio de 10 minutos).
      timeoutGravacaoRef.current = setTimeout(() => pararGravacao(), DURACAO_MAX_AUDIO_MS);
    } catch (e: any) {
      setErroGravacao('Não foi possível acessar o microfone. Verifique a permissão do navegador.');
    }
  };

  const pararGravacao = () => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') recorder.stop();
    setGravando(false);
  };


  // Legenda opcional: reaproveita o que já estiver digitado no campo de
  // texto (se houver) — evita um segundo campo/diálogo só pra isso. Sem
  // nada digitado, a foto vai sem legenda (igual ao WhatsApp).
  const selecionarImagem = () => inputImagemRef.current?.click();

  const arquivoSelecionado = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const arquivo = e.target.files?.[0];
    e.target.value = '';
    if (!arquivo) return;

    const socket = socketRef.current;
    if (!socket || !conectado || !conversaAtiva || aguardando) return;

    const base64Completo = await blobParaBase64(arquivo);
    const legenda = texto.trim() || undefined;

    setMensagens(prev => [
      ...prev,
      {
        id: idLocal(), autor: 'voce', texto: legenda || '', imagemPreviewUrl: `data:${arquivo.type};base64,${base64Completo}`,
        timestamp: new Date().toISOString(), estado: 'enviada',
      },
    ]);
    setTexto('');
    pertoDoFinalRef.current = true;
    setAguardando(true);
    setErro(null);

    socket.emit('interacao', {
      domain: dominio,
      actorId: conversaAtiva.actorId,
      projectId: conversaAtiva.projectId || undefined,
      dataScope: conversaAtiva.dataScope,
      conversationId: conversaAtiva.conversationId,
      media: { base64: base64Completo, mimeType: arquivo.type, caption: legenda },
    });

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setAguardando(false);
      setMensagens(prev => [...prev, { id: idLocal(), autor: 'sistema', texto: 'Sem resposta do motor Syncorp. Tente novamente em alguns instantes.', timestamp: new Date().toISOString() }]);
    }, AVISO_IMAGEM_MS);
  };

  const enviarLocalizacaoAtual = () => {
    const socket = socketRef.current;
    if (!socket || !conectado || !conversaAtiva || aguardando) return;
    if (!navigator.geolocation) {
      setErro('Este navegador não suporta compartilhar localização.');
      return;
    }

    setEnviandoLocalizacao(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setEnviandoLocalizacao(false);
        const { latitude, longitude, accuracy } = pos.coords;

        setMensagens(prev => [
          ...prev,
          { id: idLocal(), autor: 'voce', texto: '', localizacaoEnviada: { latitude, longitude }, timestamp: new Date().toISOString(), estado: 'enviada' },
        ]);
        pertoDoFinalRef.current = true;
        setAguardando(true);
        setErro(null);

        socket.emit('interacao', {
          domain: dominio,
          actorId: conversaAtiva.actorId,
          projectId: conversaAtiva.projectId || undefined,
          dataScope: conversaAtiva.dataScope,
          conversationId: conversaAtiva.conversationId,
          location: { latitude, longitude, accuracyM: accuracy || undefined },
        });

        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
          setAguardando(false);
          setMensagens(prev => [...prev, { id: idLocal(), autor: 'sistema', texto: 'Sem resposta do motor Syncorp. Tente novamente em alguns instantes.', timestamp: new Date().toISOString() }]);
        }, AVISO_SEM_RESPOSTA_MS);
      },
      () => {
        setEnviandoLocalizacao(false);
        setErro('Não foi possível obter sua localização. Verifique a permissão do navegador.');
      },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  };

  const encerrarConversaAtiva = async () => {
    const socket = socketRef.current;
    if (!socket || !conversaAtiva) return;
    pararAudioAtivo();
    const resp: RespostaAck<{}> = await socket.emitWithAck('conversa:encerrar', {
      domain: dominio,
      actorId: conversaAtiva.actorId,
      projectId: conversaAtiva.projectId || undefined,
      dataScope: conversaAtiva.dataScope,
      conversationId: conversaAtiva.conversationId,
    });
    if (!resp.ok) { setErro(resp.error); return; }
    setConversaAtiva(prev => (prev ? { ...prev, status: 'encerrada' } : prev));
    setConversas(prev => prev.map(c => c.conversationId === conversaAtiva.conversationId ? { ...c, status: 'encerrada' } : c));
    setMensagens(prev => [...prev, { id: idLocal(), autor: 'sistema', texto: '— Conversa encerrada —', timestamp: new Date().toISOString() }]);
  };

  const reiniciarConversaAtiva = async () => {
    const socket = socketRef.current;
    if (!socket || !conversaAtiva?.actorId) return;
    pararAudioAtivo();
    setErro(null);
    const resp: RespostaAck<{ status: StatusConversa }> = await socket.emitWithAck('conversa:reiniciar', {
      domain: dominio,
      actorId: conversaAtiva.actorId,
      projectId: conversaAtiva.projectId || undefined,
      dataScope: conversaAtiva.dataScope,
      conversationId: conversaAtiva.conversationId,
    });
    if (!resp.ok) { setErro(resp.error); return; }
    setConversaAtiva(prev => (prev ? { ...prev, status: resp.status } : prev));
    setConversas(prev => prev.map(c => c.conversationId === conversaAtiva.conversationId ? { ...c, status: resp.status } : c));
    setMensagens(prev => [...prev, { id: idLocal(), autor: 'sistema', texto: '— Conversa reiniciada —', timestamp: new Date().toISOString() }]);
  };

  const selecionarConversa = (resumo: ConversaResumo) => {
    if (!resumo.actorId) return;
    setPescadorSelecionado({ uid_pesca_pescador: resumo.actorId, nome: resumo.nomeParticipante || resumo.actorId });
    setProjetoSelecionado(resumo.projectId || '');
    // conversationId explícito: retoma ESSA conversa específica da lista —
    // um participante pode ter mais de uma (sessões novas isoladas), nunca
    // ambigua com a "padrão" (Task 4/N).
    abrirConversa(resumo.actorId, resumo.projectId || undefined, resumo.dataScope, { conversationId: resumo.conversationId });
  };

  // "Conversa só inicia com contexto válido": escolher um pescador na busca
  // só o seleciona — este botão é o único gatilho real de conversa:iniciar
  // pra uma conversa nova, garantindo que projeto/domínio/modo de uso
  // escolhidos acima sejam os que valem (nunca o que sobrou de uma seleção
  // anterior).
  // Ao escolher um projeto explícito, confere que o participante selecionado
  // está vinculado a ele (Laboratório, item 1): se não estiver, a seleção é limpa.
  const trocarProjeto = async (novoProjeto: string) => {
    setProjetoSelecionado(novoProjeto);
    if (!novoProjeto || !pescadorSelecionado) return;
    try {
      const params = new URLSearchParams({ page: '1', search: pescadorSelecionado.nome, limit: '50', uid_pesca_projeto: novoProjeto });
      const res = await api.get(`/pesca/pescadores?${params.toString()}`);
      const vinculado = (res.data?.data || []).some((x: Pescador) => x.uid_pesca_pescador === pescadorSelecionado.uid_pesca_pescador);
      if (!vinculado) setPescadorSelecionado(null);
    } catch {
      setPescadorSelecionado(null);
    }
  };

  // Sair da conversa (sem encerrá-la) libera o contexto para trocar.
  const sairDaConversa = () => {
    pararAudioAtivo();
    setModoApresentacao(false);
    setConversaAtiva(null);
    setMensagens([]);
    setMostrarConversaMobile(false);
  };

  const iniciarConversaSelecionada = () => {
    if (!pescadorSelecionado) return;
    abrirConversa(pescadorSelecionado.uid_pesca_pescador, projetoSelecionado || undefined, dataScopeSelecionado);
  };

  // "Permitir nova demonstração sem reutilizar sessão anterior" (Task
  // 4/N): sempre uma sessão ISOLADA, nunca a conversa "padrão" nem
  // nenhuma outra já existente pra este participante — só faz sentido
  // fora de Produção (mesma regra do backend).
  const iniciarNovaDemonstracao = () => {
    if (!pescadorSelecionado) return;
    abrirConversa(pescadorSelecionado.uid_pesca_pescador, projetoSelecionado || undefined, dataScopeSelecionado, { novaSessao: true });
  };

  const nomeConversaAtiva = useMemo(() => {
    if (!conversaAtiva) return '';
    return conversaAtiva.nomeParticipante || conversaAtiva.actorId || 'Conversa';
  }, [conversaAtiva]);

  const encerrada = conversaAtiva?.status === 'encerrada';

  return (
    // Os "slots" (filhos falsy) mantêm a posição dos elementos entre o modo
    // normal e a apresentação: composer, áudio e rolagem não remontam ao alternar.
    <div className={apresentando ? styles.apresentacao : `${viewStyles.container} ${styles.pageWrap}`}>
      {apresentando && (
        <div className={styles.controlesApresentacao}>
          <button type="button" className={styles.botaoControleApresentacao} onClick={alternarTelaCheia}>
            Tela cheia
          </button>
          <button type="button" className={styles.botaoControleApresentacao} onClick={sairDaApresentacao}>
            Sair da apresentação
          </button>
        </div>
      )}
      {!apresentando && (
        <div className={viewStyles.headerTitle}>
          <h1 className={`${viewStyles.greenTitle} ${styles.tituloMobile}`}>Laboratório de Interação</h1>
        </div>
      )}
      {!apresentando && (
        <p className={styles.subtitulo}>Escolha o contexto e converse direto com o motor Syncorp, sem usar o WhatsApp.</p>
      )}

      <div className={apresentando ? styles.celular : `${styles.grid} ${mostrarConversaMobile ? styles.mostrandoConversa : styles.mostrandoContatos}`}>
        {!apresentando && (
        <div className={`${viewStyles.sectionCard} ${styles.painelLista}`}>
          <div className={viewStyles.sectionHeader}>CONTEXTO DA INTERAÇÃO</div>

          <div className={styles.contextoBloco}>
            <label className={styles.rotuloContexto} htmlFor="lab-dominio">Domínio</label>
            <select id="lab-dominio" className={styles.campoSelect} value={dominio} disabled>
              <option value="pesca">Pesca</option>
              <option value="agro">Agro (em breve)</option>
            </select>

            <label className={styles.rotuloContexto} htmlFor="lab-projeto">Projeto</label>
            <select
              id="lab-projeto"
              className={styles.campoSelect}
              value={projetoSelecionado}
              onChange={(e) => trocarProjeto(e.target.value)}
              disabled={!!conversaAtiva}
            >
              <option value="">Automático (projeto ativo do participante)</option>
              {projetos.map((p) => (
                <option key={p.uid_pesca_projeto} value={p.uid_pesca_projeto}>{p.nome}</option>
              ))}
            </select>

            <label className={styles.rotuloContexto} htmlFor="lab-busca-pescador">Participante</label>
            <input
              id="lab-busca-pescador"
              className={styles.campoBusca}
              placeholder="Buscar pescador por nome"
              value={buscaPescador}
              onChange={(e) => setBuscaPescador(e.target.value)}
              disabled={!!conversaAtiva}
            />
            <div className={styles.listaPescadores}>
              {pescadores.map((p) => {
                const selecionado = pescadorSelecionado?.uid_pesca_pescador === p.uid_pesca_pescador;
                return (
                  <button
                    key={p.uid_pesca_pescador}
                    type="button"
                    className={`${styles.itemPescador} ${selecionado ? styles.itemPescadorSelecionado : ''}`}
                    onClick={() => setPescadorSelecionado(p)}
                    disabled={!!conversaAtiva}
                  >
                    {p.nome}
                  </button>
                );
              })}
              {pescadores.length === 0 && <div className={styles.listaVazia}>Nenhum pescador encontrado.</div>}
            </div>

            <label className={styles.rotuloContexto} htmlFor="lab-data-scope">Modo de uso</label>
            <select
              id="lab-data-scope"
              className={styles.campoSelect}
              value={dataScopeSelecionado}
              onChange={(e) => setDataScopeSelecionado(e.target.value as DataScope)}
              disabled={!!conversaAtiva}
            >
              <option value="teste">Teste</option>
              <option value="demonstracao">Demonstração</option>
              <option value="producao" disabled={!ehAdmin}>
                Produção{!ehAdmin ? ' (só admin)' : ''}
              </option>
            </select>

            <div className={styles.contextoAtivo}>
              Contexto {conversaAtiva ? 'da conversa' : 'selecionado'}: <strong>Pesca</strong> · Projeto {conversaAtiva
                ? (conversaAtiva.nomeProjeto || 'não identificado')
                : (projetos.find((p) => p.uid_pesca_projeto === projetoSelecionado)?.nome || 'automático (projeto ativo do participante)')} · {conversaAtiva?.nomeParticipante || pescadorSelecionado?.nome || 'nenhum participante selecionado'} · <strong>{ROTULO_DATA_SCOPE[conversaAtiva?.dataScope ?? dataScopeSelecionado]}</strong>
            </div>

            {conversaAtiva ? (
              <button type="button" className={styles.botaoIniciar} onClick={sairDaConversa}>
                Sair da conversa (para trocar contexto)
              </button>
            ) : (
            <button type="button" className={styles.botaoIniciar} onClick={iniciarConversaSelecionada} disabled={!pescadorSelecionado || !conectado}>
              Iniciar conversa
            </button>
            )}
            {dataScopeSelecionado !== 'producao' && (
              <button
                type="button"
                className={styles.botaoNovaDemonstracao}
                onClick={iniciarNovaDemonstracao}
                disabled={!pescadorSelecionado || !conectado || !!conversaAtiva}
                title="Cria uma sessão nova e isolada — não reaproveita o histórico/estado de nenhuma demonstração anterior deste participante."
              >
                Nova demonstração
              </button>
            )}
          </div>

          <div className={styles.tituloConversasAnteriores}>Conversas anteriores</div>
          <div className={styles.listaConversas}>
            {carregandoConversas ? (
              <div className={styles.mensagemEstado}>Carregando conversas...</div>
            ) : conversas.length === 0 ? (
              <div className={styles.mensagemEstado}>Nenhuma conversa ainda. Inicie uma nova acima.</div>
            ) : (
              conversas.map((c) => {
                const ativa = conversaAtiva?.conversationId === c.conversationId;
                return (
                  <button key={c.conversationId} type="button" className={`${styles.itemConversa} ${ativa ? styles.itemConversaAtiva : ''}`} onClick={() => selecionarConversa(c)}>
                    <strong className={styles.nomeItemConversa}>
                      {c.nomeParticipante || c.actorId || 'Participante'}
                      {c.dataScope !== 'producao' && <span className={styles.seloDataScope}>{ROTULO_DATA_SCOPE[c.dataScope]}</span>}
                      {c.status === 'encerrada' && <span className={styles.selo}>encerrada</span>}
                    </strong>
                    {c.nomeProjeto && <span className={styles.subtextoItemConversa}>{c.nomeProjeto}</span>}
                    {c.nomeOperador && <span className={styles.subtextoItemConversa}>Iniciada por {c.nomeOperador}</span>}
                    <span className={styles.subtextoItemConversa}>Última interação: {formatarDataHora(c.ultimaInteracao)}</span>
                  </button>
                );
              })
            )}
          </div>
        </div>
        )}

        <div className={`${viewStyles.sectionCard} ${styles.painelChat}`}>
          {apresentando ? (
            <div className={styles.cabecalhoApresentacao}>
              <img src={`${process.env.PUBLIC_URL}/logo512.png`} alt="" className={styles.logoApresentacao} />
              <div className={styles.identidadeApresentacao}>
                <span className={styles.nomeApresentacao}>Syncorp</span>
                <span className={styles.statusApresentacao}>{conectado ? 'online' : 'reconectando...'}</span>
              </div>
              {conversaAtiva && conversaAtiva.dataScope !== 'producao' && (
                <span className={styles.seloApresentacao}>{ROTULO_DATA_SCOPE[conversaAtiva.dataScope]}</span>
              )}
            </div>
          ) : (
          <div className={`${viewStyles.sectionHeader} ${styles.cabecalhoChat}`}>
            <button type="button" className={styles.botaoVoltar} onClick={() => setMostrarConversaMobile(false)} aria-label="Voltar para a lista de conversas">
              ‹ Conversas
            </button>
            <span className={styles.tituloChatDesktop}>CONVERSA</span>
            <div className={styles.infoChatCabecalho}>
              <span className={styles.nomeChatCabecalho}>
                {conversaAtiva ? nomeConversaAtiva : 'Selecione ou inicie uma conversa'}
              </span>
              {conversaAtiva && conversaAtiva.dataScope !== 'producao' && (
                <span className={styles.seloDataScope}>{ROTULO_DATA_SCOPE[conversaAtiva.dataScope]}</span>
              )}
              <span className={conectado ? styles.statusOn : styles.statusOff}>{conectado ? 'conectado' : 'desconectado'}</span>
              {conversaAtiva && (
                <button type="button" className={styles.botaoAcaoConversa} onClick={iniciarApresentacao}>Apresentar</button>
              )}
              {conversaAtiva && (
                encerrada ? (
                  <button type="button" className={styles.botaoAcaoConversa} onClick={reiniciarConversaAtiva}>Reiniciar</button>
                ) : (
                  <button type="button" className={`${styles.botaoAcaoConversa} ${styles.botaoAcaoConversaEncerrar}`} onClick={encerrarConversaAtiva}>Encerrar</button>
                )
              )}
            </div>
          </div>
          )}

          {/* "modo não-produção inequívoco" (Task 4/N): faixa CHEIA, não só
              um selo — impossível de confundir com uma conversa real
              enquanto ela rola por baixo. Na apresentação vira só o selo
              discreto do cabeçalho. */}
          {!apresentando && conversaAtiva && conversaAtiva.dataScope !== 'producao' && (
            <div className={styles.faixaModoNaoProducao}>
              MODO {ROTULO_DATA_SCOPE[conversaAtiva.dataScope].toUpperCase()} — esta conversa não conta como dado de produção
              {conversaAtiva.nomeOperador ? ` · iniciada por ${conversaAtiva.nomeOperador}` : ''}
            </div>
          )}

          {erro && <div className={styles.faixaErro}>{erro}</div>}

          <div className={styles.areaMensagens} ref={chatRef}>
            {!conversaAtiva ? (
              <div className={styles.mensagemEstadoCentral}>Selecione uma conversa à esquerda ou inicie uma nova.</div>
            ) : carregandoConversa ? (
              <div className={styles.mensagemEstadoCentral}>Carregando conversa...</div>
            ) : mensagens.length === 0 ? (
              <div className={styles.mensagemEstadoCentral}>Diga "oi" para começar.</div>
            ) : (
              mensagens.map((m, index) => {
                const anterior = index > 0 ? mensagens[index - 1] : null;
                const mudouDia = !anterior || chaveDia(anterior.timestamp) !== chaveDia(m.timestamp);

                if (m.autor === 'sistema') {
                  return (
                    <div key={m.id} className={styles.avisoSistema}>{m.texto}</div>
                  );
                }

                return (
                  <React.Fragment key={m.id}>
                    {mudouDia && <div className={styles.separadorDia}>{formatarDataSeparador(m.timestamp)}</div>}
                    <div className={`${styles.linhaBalao} ${m.autor === 'voce' ? styles.linhaVoce : styles.linhaSyncorp}`}>
                      <div className={`${styles.balao} ${m.autor === 'voce' ? styles.balaoVoce : styles.balaoSyncorp}`}>
                        {!apresentando && <div className={styles.autorBalao}>{m.autor === 'voce' ? 'Você' : 'Syncorp'}</div>}
                        {m.ehAudioEnviado || m.audioSrc || m.temAudioServidor ? (
                          <PlayerAudio
                            src={m.audioSrc}
                            uidAudio={m.temAudioServidor ? m.uidMensagem : undefined}
                            transcricao={m.transcricao ?? (m.autor === 'syncorp' ? m.texto : undefined)}
                            falhaTranscricao={m.falhaTranscricao}
                            autoPlay={m.autoplay}
                          />
                        ) : m.imagemPreviewUrl ? (
                          <>
                            <img src={m.imagemPreviewUrl} alt="Foto enviada" className={styles.imagemBalao} />
                            {m.texto && <div className={styles.textoBalao}>{m.texto}</div>}
                          </>
                        ) : m.localizacaoEnviada ? (
                          <a
                            className={styles.linkLocalizacao}
                            href={`https://www.google.com/maps?q=${m.localizacaoEnviada.latitude},${m.localizacaoEnviada.longitude}`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            📍 Localização enviada — ver no mapa
                          </a>
                        ) : (
                          <div className={styles.textoBalao}>{m.texto}</div>
                        )}
                        {m.arquivo && (
                          <div className={styles.arquivoBalao}>
                            <span className={styles.arquivoNome}>📄 {m.arquivo.filename}</span>
                            <button type="button" className={styles.chipOpcao} onClick={() => abrirArquivo(m.arquivo!, false)}>Abrir</button>
                            <button type="button" className={styles.chipOpcao} onClick={() => abrirArquivo(m.arquivo!, true)}>Baixar</button>
                          </div>
                        )}
                        {m.opcoes && (
                          <div className={styles.opcoesBalao}>
                            {m.opcoes.map((o) => (
                              <button key={o} type="button" className={styles.chipOpcao} onClick={() => enviarMensagem(o)} disabled={aguardando}>{o}</button>
                            ))}
                          </div>
                        )}
                        <div className={styles.horaBalao}>
                          {formatarHora(m.timestamp)}
                          {m.estado === 'enviando' && ' · enviando...'}
                        </div>
                      </div>
                    </div>
                  </React.Fragment>
                );
              })
            )}
            {aguardando && (
              <div className={`${styles.linhaBalao} ${styles.linhaSyncorp}`}>
                <div className={`${styles.balao} ${styles.balaoSyncorp} ${styles.balaoProcessando}`}>Syncorp está processando...</div>
              </div>
            )}
          </div>

          {erroGravacao && <div className={styles.faixaErro}>{erroGravacao}</div>}

          <div className={styles.composer}>
            <textarea
              ref={textareaRef}
              className={styles.campoTexto}
              placeholder={
                gravando
                  ? 'Gravando áudio...'
                  : conversaAtiva ? (encerrada ? 'Conversa encerrada — reinicie para continuar' : 'Digite uma mensagem') : 'Selecione uma conversa'
              }
              value={texto}
              onChange={(e) => {
                setTexto(e.target.value);
                e.target.style.height = 'auto';
                e.target.style.height = `${Math.min(e.target.scrollHeight, 140)}px`;
              }}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviarMensagem(texto); } }}
              disabled={!conectado || !conversaAtiva || encerrada || gravando}
              rows={1}
            />
            <input
              ref={inputImagemRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={arquivoSelecionado}
            />
            <button
              type="button"
              className={styles.botaoMicrofone}
              onClick={selecionarImagem}
              disabled={!conectado || !conversaAtiva || encerrada || aguardando || gravando}
              title="Enviar foto"
            >
              📷
            </button>
            <button
              type="button"
              className={styles.botaoMicrofone}
              onClick={enviarLocalizacaoAtual}
              disabled={!conectado || !conversaAtiva || encerrada || aguardando || gravando || enviandoLocalizacao}
              title="Enviar localização atual"
            >
              {enviandoLocalizacao ? '…' : '📍'}
            </button>
            <button
              type="button"
              className={`${styles.botaoMicrofone} ${gravando ? styles.botaoMicrofoneGravando : ''}`}
              onClick={gravando ? pararGravacao : iniciarGravacao}
              disabled={!conectado || !conversaAtiva || encerrada || aguardando}
              title={gravando ? 'Parar e enviar gravação' : 'Gravar mensagem de áudio'}
            >
              {gravando ? '⏹' : '🎤'}
            </button>
            <button type="button" className={styles.botaoEnviar} onClick={() => enviarMensagem(texto)} disabled={!conectado || !conversaAtiva || encerrada || aguardando || gravando || !texto.trim()}>
              Enviar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatSyncorpPage;
